import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { chatResourceFilename, chatTranscriptToMarkdown } from "~/ai-chat/model/chat-transcript-markdown";
import { useCreateResource } from "~/resource/data-source/use-create-resource";
import { routes } from "~/routes";

/**
 * Save the current chat transcript as a Markdown resource in the user's
 * workspace, then toast a link to the created file.
 */
export function useSaveChatResource() {
  const createResource = useCreateResource();
  const navigate = useNavigate();

  const save = useCallback(
    (title: string, items: ChatTranscriptItem[]) => {
      const displayName = title.trim() || "Istari chat";
      const markdown = chatTranscriptToMarkdown(displayName, items);
      const file = new File([markdown], chatResourceFilename(displayName), { type: "text/markdown" });

      createResource.mutate(
        { file, displayName, resourceType: V3Models.ResourceTypeDto.File },
        {
          onSuccess: (resource) => {
            toast.success("Chat saved to your workspace.", {
              action: { label: "Open", onClick: () => navigate(`${routes.Files}/${resource.resourceId}`) },
            });
          },
          onError: () => toast.error("Couldn't save the chat. Please try again."),
        },
      );
    },
    [createResource, navigate],
  );

  return { save, isSaving: createResource.isPending };
}
